import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { useQuery } from '@tanstack/react-query';
import { usersApi, type PunchListResponse } from '@/lib/apiClient';
import { useGraphToken, useThumbnailCache } from '@/lib/graphHooks';
import {
  STAGE_LABELS, summarize, bySub, isMineToVerify, phoneMatches, roomGroups, roomTitle, roomKey, beforePhotoUrl,
  type PhoneFilter,
} from './punchModel';
import { StatusBadge, BicChip, OutdoorToggle } from './punchUi';

type Data = PunchListResponse & { list: NonNullable<PunchListResponse['list']> };

/**
 * The Punch List on a phone (#518, design "Punch List: mobile", grouped by
 * room): made for the building walk. Summary tiles and quick chips filter
 * one way at a time; items are grouped by room, each group with Add Here;
 * Add Item stays at the bottom. Tapping an item opens the same item drawer
 * the desktop uses, full width. Settings, publishing and sub sheets stay on
 * the desktop.
 */
export default function PunchPhoneList({ data, canWork, jobFolderUrl, onOpenItem, onAdd, onDrawings }: {
  data: Data;
  canWork: boolean;
  jobFolderUrl: string | null;
  onOpenItem: (id: number) => void;
  /** Open Add Item, with a room already set (Add Here) or not. */
  onAdd: (room?: string) => void;
  /** Open the drawing sets, with every item's pin (#518 phase 3). */
  onDrawings?: () => void;
}) {
  const { list, items } = data;
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const meId = me?.id ?? null;
  const [filter, setFilter] = useState<PhoneFilter>({ kind: 'all' });
  const [sheet, setSheet] = useState<'sub' | 'room' | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const toggle = (f: PhoneFilter) => setFilter((cur) => (cur.kind === f.kind ? { kind: 'all' } : f));

  const s = useMemo(() => summarize(items), [items]);
  // Counted the way this list counts "open": everything not closed, Ready for
  // review and Verified included, so a sub whose items are all being checked
  // is still pickable and its count matches what picking it shows (Codex on #569).
  const subs = useMemo(
    () => bySub(items).map((r) => ({ ...r, live: r.total - r.closed })).filter((r) => r.live > 0),
    [items],
  );
  const allRooms = useMemo(() => roomGroups(items.filter((i) => i.status !== 'closed')), [items]);
  const verifyCount = useMemo(() => items.filter((i) => isMineToVerify(i, meId)).length, [items, meId]);

  const matching = useMemo(() => items.filter((i) => phoneMatches(i, filter, meId)), [items, filter, meId]);
  const open = useMemo(() => matching.filter((i) => i.status !== 'closed'), [matching]);
  const closed = useMemo(() => matching.filter((i) => i.status === 'closed'), [matching]);
  const groups = useMemo(() => roomGroups(showClosed ? matching : open), [matching, open, showClosed]);

  const graph = useGraphToken();
  const thumbUrls = useMemo(
    () => groups.flatMap((g) => g.items.map(beforePhotoUrl).filter((u): u is string => !!u)),
    [groups],
  );
  const { thumbs, reset } = useThumbnailCache(thumbUrls, graph.silent);

  const pct = s.total ? Math.round((s.closed / s.total) * 100) : 0;
  const canAdd = canWork && !!jobFolderUrl;
  const chipLabel = filter.kind === 'sub' ? filter.label : filter.kind === 'room' ? roomTitle(filter.room) : null;

  return (
    <div className="pl-phone">
      <div className="pl-phone-stage">
        <span className="pl-phone-stage-chip">{STAGE_LABELS[list.stage]}</span>
        <span className="pl-muted">{s.closed} of {s.total} closed</span>
        <OutdoorToggle />
        <div className="pl-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="pl-phone-tiles">
        <Tile label="Waiting on us" value={s.onUs.total} active={filter.kind === 'on_us'} onClick={() => toggle({ kind: 'on_us' })} />
        <Tile label="On subs" value={s.onSubs.total} active={filter.kind === 'on_subs'} onClick={() => toggle({ kind: 'on_subs' })} />
        <Tile label="Overdue" value={s.overdue} danger={s.overdue > 0} active={filter.kind === 'overdue'} onClick={() => toggle({ kind: 'overdue' })} />
      </div>

      <div className="pl-phone-chips" role="group" aria-label="Quick filters">
        <Chip active={filter.kind === 'all'} onClick={() => setFilter({ kind: 'all' })}>All</Chip>
        <Chip active={filter.kind === 'verify'} onClick={() => toggle({ kind: 'verify' })}>Mine to verify ({verifyCount})</Chip>
        <Chip active={filter.kind === 'overdue'} onClick={() => toggle({ kind: 'overdue' })}>Overdue ({s.overdue})</Chip>
        <Chip active={filter.kind === 'sub'} onClick={() => setSheet('sub')}>{filter.kind === 'sub' ? chipLabel : 'By sub'} ▾</Chip>
        <Chip active={filter.kind === 'room'} onClick={() => setSheet('room')}>{filter.kind === 'room' ? chipLabel : 'By room'} ▾</Chip>
      </div>

      <div className="pl-row" style={{ justifyContent: 'space-between' }}>
        <span className="pl-phone-count">{open.length} open</span>
        {onDrawings && <button type="button" className="pl-link" onClick={onDrawings}>📍 Drawings</button>}
        {filter.kind !== 'all' && <button type="button" className="pl-link" onClick={() => setFilter({ kind: 'all' })}>Clear filter</button>}
        {graph.needsSignIn && thumbUrls.length > 0 && (
          <button type="button" className="pl-link" onClick={async () => { try { await graph.interactive(); reset(); } catch { /* next tap */ } }}>
            Show photos
          </button>
        )}
      </div>

      {canWork && !jobFolderUrl && (
        <div className="pl-warn">This job’s Job Folder isn’t linked yet, so items can’t be added — photos are saved there.</div>
      )}

      {groups.length === 0 && (
        <p className="pl-muted">{items.length === 0 ? 'No items yet. Add the first one from the building walk.' : 'No items match.'}</p>
      )}

      {groups.map((g) => (
        <section key={g.key} className="pl-card pl-room" aria-label={roomTitle(g.room)}>
          <div className="pl-room-hd">
            <div>
              <div className="pl-room-title">{roomTitle(g.room)}</div>
              <div className="pl-muted">{g.open} open</div>
            </div>
            {canAdd && (
              <button type="button" className="pl-phone-btn secondary" onClick={() => onAdd(g.room)}>＋ Add Here</button>
            )}
          </div>
          {g.items.map((i) => (
            <button key={i.id} type="button" className="pl-room-row" onClick={() => onOpenItem(i.id)} aria-label={`Open ${i.label}`}>
              {thumbs[beforePhotoUrl(i) ?? ''] ? <img className="pl-room-thumb" src={thumbs[beforePhotoUrl(i)!]} alt="" /> : <span className="pl-room-thumb" aria-hidden />}
              <span className="pl-room-main">
                <span className="pl-room-desc">{i.description}</span>
                <span className="pl-room-meta">
                  <span className="ew-num">{i.label}</span>
                  <StatusBadge status={i.status} />
                  <BicChip type={i.bic.type} label={i.bic.label} />
                </span>
              </span>
              <span className="pl-room-chev" aria-hidden>›</span>
            </button>
          ))}
        </section>
      ))}

      {closed.length > 0 && (
        <button type="button" className="pl-phone-more" onClick={() => setShowClosed((v) => !v)}>
          {showClosed ? 'Hide closed items' : `Show ${closed.length} closed item${closed.length === 1 ? '' : 's'}`}
        </button>
      )}

      {canAdd && (
        <div className="pl-phone-bottom">
          <button type="button" className="pl-phone-btn primary wide" onClick={() => onAdd()}>＋ Add Item</button>
        </div>
      )}

      {sheet === 'sub' && (
        <BottomSheet title="By sub" onClose={() => setSheet(null)}>
          {subs.length === 0 && <p className="pl-muted">Nothing open is assigned yet.</p>}
          {subs.map((r) => (
            <button key={r.key} type="button" className="pl-sheet-pick"
              onClick={() => { setFilter({ kind: 'sub', key: r.key, label: r.label }); setSheet(null); }}>
              <span>{r.label}</span><span className="pl-muted">{r.live} open</span>
            </button>
          ))}
        </BottomSheet>
      )}
      {sheet === 'room' && (
        <BottomSheet title="By room" onClose={() => setSheet(null)}>
          {allRooms.length === 0 && <p className="pl-muted">Nothing open yet.</p>}
          <div className="pl-room-grid">
            {allRooms.map((g) => (
              <button key={g.key} type="button" className="pl-room-cell"
                aria-pressed={filter.kind === 'room' && roomKey(filter.room) === g.key}
                onClick={() => { setFilter({ kind: 'room', room: g.room }); setSheet(null); }}>
                <span className="pl-room-cell-name">{g.room || 'No room'}</span>
                <span className="pl-muted">{g.open} open</span>
              </button>
            ))}
          </div>
        </BottomSheet>
      )}
    </div>
  );
}

function Tile({ label, value, active, danger = false, onClick }: {
  label: string; value: number; active: boolean; danger?: boolean; onClick: () => void;
}) {
  return (
    <button type="button" className={`pl-phone-tile${active ? ' active' : ''}`} aria-pressed={active} onClick={onClick}>
      <span className="pl-phone-tile-v" style={danger ? { color: 'var(--ew-danger-700)' } : undefined}>{value}</span>
      <span className="pl-phone-tile-l">{label}</span>
    </button>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className={`pl-phone-chip${active ? ' active' : ''}`} aria-pressed={active} onClick={onClick}>
      {children}
    </button>
  );
}

/** A sheet from the bottom of the screen; the scrim or Done closes it. */
export function BottomSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  // A dialog: focus moves in and stays until it closes, Escape closes it
  // (Codex on #569).
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, { onEscape: onClose });
  return (
    <>
      <div className="pl-bsheet-scrim" onClick={onClose} />
      <div ref={ref} className="pl-bsheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="pl-bsheet-grab" aria-hidden />
        <div className="pl-row" style={{ justifyContent: 'space-between' }}>
          <strong>{title}</strong>
          <button type="button" className="pl-link" onClick={onClose}>Done</button>
        </div>
        <div className="pl-bsheet-body">{children}</div>
      </div>
    </>
  );
}
